import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Button,
  TextField,
  InputAdornment,
  Grid,
  IconButton,
  Tooltip,
  TablePagination,
  Avatar,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  Refresh,
  Search,
  Person,
  Email,
  Business,
  Phone,
  CalendarToday,
  Visibility,
  People,
  TrendingUp,
  CheckCircle,
  InfoOutlined,
  Close,
  AccountBalance,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import UserService from '../../services/userService';
import useRoleColors from '../../hooks/useRoleColors';

const AllUsers = ({ user }) => {
  const {
    accent,
    accentLight,
    accentDark,
    accentGradient,
    accentGradientHover,
    softBg,
    softBgHover,
    softBgStrong,
    contrast,
    outline,
  } = useRoleColors();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [userDialog, setUserDialog] = useState({ open: false, user: null });
  const [editForm, setEditForm] = useState({
    name: '',
    surname: '',
    email: '',
    phoneNumber: '',
    companyName: '',
    taxNumber: '',
    companyType: '',
    businessArea: '',
    city: '',
    address: '',
    title: '',
    ibanNumber: '',
  });
  const [updatingUser, setUpdatingUser] = useState(false);

  // Fetch all users
  const fetchUsers = async () => {
    setLoading(true);
    setError('');

    const result = await UserService.getAllUsers(page + 1, rowsPerPage);
    
    if (result.success) {
      setUsers(result.data);
      setTotalUsers(result.pagination?.total || 0);
    } else {
      setError(result.message);
    }
    
    setLoading(false);
  };

  // Load users on component mount and page change
  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage]);

  // Filter users based on search term
  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.surname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.userId?.toString().includes(searchTerm)
  );

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 1: return 'success'; // Active
      case 0: return 'warning'; // Inactive
      case 2: return 'info'; // Pending
      case 3: return 'error'; // Suspended
      case -1: return 'default'; // Deleted
      default: return 'default';
    }
  };

  // Get status text
  const getStatusText = (status) => {
    switch (status) {
      case 1: return 'Active';
      case 0: return 'Inactive';
      case 2: return 'Pending';
      case 3: return 'Suspended';
      case -1: return 'Deleted';
      default: return 'Unknown';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US');
  };

  // Get user stats
  const getUserStats = () => {
    const active = filteredUsers.filter(u => u.status === 1).length;
    const total = filteredUsers.length;
    return { active, total };
  };

  // Format IBAN input (mask)
  const formatIbanInput = (value) => {
    if (!value) return '';
    const cleanValue = value
      .replace(/\s+/g, '')
      .replace(/[^0-9A-Za-z]/g, '')
      .toUpperCase()
      .slice(0, 26);
    return cleanValue.replace(/(.{4})/g, '$1 ').trim();
  };

  // Sanitize IBAN (remove spaces and special characters for backend)
  const sanitizeIban = (value) =>
    value ? value.replace(/[^0-9A-Za-z]/g, '').toUpperCase().slice(0, 26) : '';

  // Handle view user details
  const handleViewUser = async (user) => {
    setUserDialog({ open: true, user });
    // Fetch full user details
    const result = await UserService.getUserDetailsByAdmin(user.userId);
    if (result.success && result.data) {
      const userData = result.data;
      setEditForm({
        name: userData.name || '',
        surname: userData.surname || '',
        email: userData.email || '',
        phoneNumber: userData.phoneNumber || '',
        companyName: userData.companyName || '',
        taxNumber: userData.taxNumber || '',
        companyType: userData.companyType || '',
        businessArea: userData.businessArea || '',
        city: userData.city || '',
        address: userData.address || '',
        title: userData.title || '',
        ibanNumber: userData.ibanNumber ? formatIbanInput(userData.ibanNumber) : '',
      });
    } else {
      // Fallback to basic user data if detailed fetch fails
      setEditForm({
        name: user.name || '',
        surname: user.surname || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        companyName: user.companyName || '',
        taxNumber: '',
        companyType: '',
        businessArea: '',
        city: '',
        address: '',
        title: '',
        ibanNumber: '',
      });
    }
  };

  // Handle form change
  const handleFormChange = (field, value) => {
    const nextValue = field === 'ibanNumber' ? formatIbanInput(value) : value;
    setEditForm(prev => ({
      ...prev,
      [field]: nextValue
    }));
  };

  // Handle update user
  const handleUpdateUser = async () => {
    if (!userDialog.user) return;
    
    setUpdatingUser(true);
    
    // Sanitize IBAN before sending to backend
    const sanitizedIbanValue = sanitizeIban(editForm.ibanNumber);
    if (sanitizedIbanValue && sanitizedIbanValue.length > 32) {
      toast.error('IBAN number cannot exceed 32 characters.');
      setUpdatingUser(false);
      return;
    }
    
    const updateData = {
      ...editForm,
      ibanNumber: sanitizedIbanValue || null,
    };
    
    const result = await UserService.updateUserByAdmin(userDialog.user.userId, updateData);
    
    if (result.success) {
      await fetchUsers();
      setUserDialog({ open: false, user: null });
    }
    
    setUpdatingUser(false);
  };

  // Handle status change
  const handleStatusChange = async (userId, newStatus) => {
    const result = await UserService.changeUserStatus(userId, newStatus);
    if (result.success) {
      await fetchUsers();
    }
  };

  // Close dialog
  const closeUserDialog = () => {
    setUserDialog({ open: false, user: null });
    setEditForm({
      name: '',
      surname: '',
      email: '',
      phoneNumber: '',
      companyName: '',
      taxNumber: '',
      companyType: '',
      businessArea: '',
      city: '',
      address: '',
      title: '',
      ibanNumber: '',
    });
  };

  const stats = getUserStats();

  return (
    <Box sx={{ 
      minHeight: '100vh',
      py: 4,
      px: { xs: 2, sm: 3 }
    }}>
      <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
        
        {/* Main Card */}
        <Card elevation={24} sx={{ 
          borderRadius: 4,
          overflow: 'hidden'
        }}>
          
          {/* Header Banner */}
          <Box sx={{ 
            background: accentGradient,
            p: 3,
            color: 'white'
          }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Avatar sx={{ 
                  width: 64, 
                  height: 64,
                  background: 'rgba(255,255,255,0.25)',
                  border: '3px solid rgba(255,255,255,0.3)'
                }}>
                  <People sx={{ fontSize: 32 }} />
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                  User Management
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  View and manage all system users
                </Typography>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <Refresh />}
                  onClick={fetchUsers}
                  disabled={loading}
                  sx={{
                    background: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    '&:hover': {
                      background: 'rgba(255,255,255,0.3)',
                    }
                  }}
                >
                  {loading ? 'Loading...' : 'Refresh'}
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Content */}
          <CardContent sx={{ p: 4 }}>

        {/* Summary Cards */}
        <Box sx={{ 
          mb: 4,
          display: 'grid',
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(4, 1fr)' 
          },
          gap: 3,
          gridAutoRows: '1fr'
        }}>
          <Card elevation={8} sx={{ 
            borderRadius: 4,
            transition: 'all 0.3s ease',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
            }
          }}>
            <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  backgroundColor: softBg,
                  mr: 2
                }}>
                  <People sx={{ color: accent, fontSize: 28 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Total Users
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: accent, mb: 1 }}>
                {stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Registered users
              </Typography>
            </CardContent>
          </Card>
          
          <Card elevation={8} sx={{ 
            borderRadius: 4,
            transition: 'all 0.3s ease',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
            }
          }}>
            <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                  mr: 2
                }}>
                  <CheckCircle sx={{ color: 'success.main', fontSize: 28 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Active Users
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main', mb: 1 }}>
                {stats.active}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {((stats.active / stats.total) * 100 || 0).toFixed(1)}% of total
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Search and Actions */}
        <Card elevation={8} sx={{ mb: 3, borderRadius: 3 }}>
          <CardContent sx={{ p: 3, backgroundColor: 'grey.50' }}>
            <TextField
              fullWidth
              placeholder="Search by Name, Email, Company, or User ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="primary" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  '&:hover fieldset': {
                    borderColor: accent,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: accent,
                  }
                }
              }}
            />
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Users Table */}
        <Card elevation={8} sx={{ borderRadius: 3 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>User ID</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Full Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Company</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'center' }}>Active/Inactive</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Registration Date</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ textAlign: 'center', py: 8 }}>
                      <CircularProgress size={40} sx={{ color: accent }} />
                      <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                        Loading users...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ textAlign: 'center', py: 8 }}>
                      <Box sx={{ 
                        display: 'inline-flex', 
                        p: 3, 
                        borderRadius: 3, 
                        backgroundColor: 'grey.100',
                        mb: 2
                      }}>
                        <People sx={{ fontSize: 48, color: 'grey.400' }} />
                      </Box>
                      <Typography variant="h6" sx={{ mb: 1, color: 'text.primary' }}>
                        No Users Found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {searchTerm ? 'No users match your search criteria.' : 'No users have been registered yet.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow 
                      key={user.id} 
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: softBg,
                        }
                      }}
                    >
                      <TableCell>
                        <Chip 
                          label={`#${user.userId}`} 
                          size="small" 
                          sx={{ 
                            fontWeight: 600, 
                            backgroundColor: softBg,
                            color: accent,
                            borderRadius: 2
                          }} 
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {user.name} {user.surname}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            {user.companyName || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            {user.phoneNumber || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusText(user.status)}
                          color={getStatusColor(user.status)}
                          size="small"
                          sx={{ 
                            fontWeight: 600,
                            borderRadius: 2,
                            color: 'white'
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={user.status === 1}
                              onChange={(e) => {
                                const newStatus = e.target.checked ? 1 : 0;
                                handleStatusChange(user.userId, newStatus);
                              }}
                              color="primary"
                            />
                          }
                          sx={{ m: 0 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(user.createdOn)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details" arrow>
                          <IconButton 
                            size="small"
                            onClick={() => handleViewUser(user)}
                            sx={{
                              color: accent,
                              '&:hover': {
                                backgroundColor: softBg,
                              }
                            }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          {!loading && filteredUsers.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalUsers}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Rows per page:"
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
              }
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontWeight: 500,
                  color: 'text.secondary'
                }
              }}
            />
          )}
        </Card>

        {/* User Details Dialog */}
        <Dialog
          open={userDialog.open}
          onClose={closeUserDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
            }
          }}
        >
          <DialogTitle sx={{ 
            background: accentGradient,
            color: 'white',
            p: { xs: 2, sm: 2.5, md: 3 }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Person sx={{ mr: { xs: 1, sm: 1.5 }, fontSize: { xs: 24, sm: 26, md: 28 } }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>
                  User Details
                </Typography>
              </Box>
              <IconButton
                onClick={closeUserDialog}
                sx={{
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  }
                }}
              >
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          
          <DialogContent sx={{ p: { xs: 2, sm: 3, md: 4 }, mt: 2 }}>
            {userDialog.user && (
              <Alert 
                severity="info" 
                icon={<Person />}
                sx={{ mb: 3, borderLeft: '4px solid', borderColor: 'primary.main' }}
              >
                Editing user: <strong>{userDialog.user.name} {userDialog.user.surname}</strong> (ID: {userDialog.user.userId})
              </Alert>
            )}
            
            <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ alignItems: 'stretch' }}>
              <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                <TextField
                  fullWidth
                  label="Name"
                  value={editForm.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person color="primary" />
                      </InputAdornment>
                    )
                  }}
                  sx={{ flex: 1 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                <TextField
                  fullWidth
                  label="Surname"
                  value={editForm.surname}
                  onChange={(e) => handleFormChange('surname', e.target.value)}
                  required
                  sx={{ flex: 1 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                <TextField
                  fullWidth
                  label="Email"
                  value={editForm.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  required
                  type="email"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email color="primary" />
                      </InputAdornment>
                    )
                  }}
                  sx={{ flex: 1 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={editForm.phoneNumber}
                  onChange={(e) => handleFormChange('phoneNumber', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone color="primary" />
                      </InputAdornment>
                    )
                  }}
                  sx={{ flex: 1 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={editForm.companyName}
                  onChange={(e) => handleFormChange('companyName', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Business color="primary" />
                      </InputAdornment>
                    )
                  }}
                  sx={{ flex: 1 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                <TextField
                  fullWidth
                  label="Tax Number"
                  value={editForm.taxNumber}
                  onChange={(e) => handleFormChange('taxNumber', e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                <TextField
                  fullWidth
                  label="City"
                  value={editForm.city}
                  onChange={(e) => handleFormChange('city', e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                <TextField
                  fullWidth
                  label="IBAN Number"
                  value={editForm.ibanNumber}
                  onChange={(e) => handleFormChange('ibanNumber', e.target.value)}
                  placeholder="TR44 4444 4444 4444 4444 4444 44"
                  helperText="Supports international IBAN formats."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccountBalance color="primary" />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: accent,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: accent,
                      }
                    },
                    '& .MuiOutlinedInput-input': {
                      fontFamily: "'Roboto Mono', 'Fira Code', monospace",
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      letterSpacing: { xs: '0.08rem', sm: '0.12rem' },
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                <TextField
                  fullWidth
                  label="Company Type"
                  value={editForm.companyType}
                  onChange={(e) => handleFormChange('companyType', e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                <TextField
                  fullWidth
                  label="Business Area"
                  value={editForm.businessArea}
                  onChange={(e) => handleFormChange('businessArea', e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                <TextField
                  fullWidth
                  label="Title"
                  value={editForm.title}
                  onChange={(e) => handleFormChange('title', e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Grid>
              
              <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                <TextField
                  fullWidth
                  label="Address"
                  value={editForm.address}
                  onChange={(e) => handleFormChange('address', e.target.value)}
                  multiline
                  rows={3}
                  sx={{ flex: 1 }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ p: { xs: 2, sm: 2.5, md: 3 }, pt: 0 }}>
            <Button 
              onClick={closeUserDialog}
              variant="outlined"
              sx={{
                borderColor: accent,
                color: accent,
                '&:hover': {
                  borderColor: accentLight,
                  backgroundColor: softBg,
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateUser} 
              variant="contained"
              disabled={updatingUser}
              startIcon={updatingUser ? <CircularProgress size={20} /> : <CheckCircle />}
              sx={{
                background: accentGradient,
                boxShadow: `0 4px 15px ${outline}`,
                '&:hover': {
                  background: accentGradientHover,
                  boxShadow: `0 6px 20px ${outline}`,
                },
                '&:disabled': {
                  background: softBgStrong,
                }
              }}
            >
              {updatingUser ? 'Updating...' : 'Update User'}
            </Button>
          </DialogActions>
        </Dialog>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default AllUsers;